import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Clock, ArrowRight } from "lucide-react";

/** Current membership state — deliberately separate from Profile verification.
 *  Free Pro / Pro Plus — Trial / Pro Plus — Active, read from the entitlement
 *  fields the backend already decorates onto the user (never inferred). */
export default function MembershipCard({ user }) {
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);

  useEffect(() => {
    api.get("/config/public").then(r => setProgram(r.data.pro_plus_program)).catch(() => {});
  }, []);

  const paused = program ? program.enrollment_enabled === false : false;
  const paid = !!user.is_pro_plus;
  const source = user.pro_plus_source;          // subscription | trial | beta | none
  const days = user.trial?.trial_days_remaining;

  const plan = paid
    ? "Pro Plus — Active"
    : source === "trial" ? "Pro Plus — Trial"
    : source === "beta" ? "Pro Plus — Beta Access"
    : "Free Pro";
  const isPlus = paid || source === "trial" || source === "beta";

  const pill = paid
    ? (user.pro_plus_status === "past_due" ? "Past due"
       : user.pro_plus_cancel_at_period_end ? "Ending soon" : "Active")
    : source === "trial" ? (days != null ? `${days} day${days === 1 ? "" : "s"} left` : "Trial")
    : source === "beta" ? "Beta"
    : "No subscription";

  const line = paid
    ? "Every Pro Plus feature is switched on. Manage or cancel anytime."
    : source === "trial"
      ? "Pro Plus features are on for your trial. No card entered — you won't be charged, and you'll return to Free Pro when it ends."
      : source === "beta"
        ? "Complimentary beta access to Pro Plus features. No card entered — you won't be charged."
        : "Your profile, posts, reviews and Requests are free forever. Pro Plus is an optional upgrade.";

  return (
    <section
      className="mb-6 bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-black/5 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid="membership-card"
    >
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#2D2926]/45 font-semibold mb-1">Membership</p>
        <div className="flex items-center gap-2 flex-wrap">
          {isPlus
            ? <Sparkles size={19} className="text-[#245544] shrink-0"/>
            : <span className="w-2.5 h-2.5 rounded-full bg-[#C79B6B] shrink-0"/>}
          <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="membership-plan-name">{plan}</h3>
          <span
            className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
              isPlus ? "bg-[#E9F0EC] text-[#245544]" : "bg-black/[0.06] text-[#2D2926]/60"
            }`}
            data-testid="membership-status-pill"
          >
            {pill}
          </span>
        </div>
        <p className="text-sm text-[#2D2926]/60 mt-1.5 max-w-2xl">{line}</p>
      </div>

      <div className="shrink-0">
        {paid ? (
          <button
            onClick={() => navigate("/settings?tab=billing")}
            className="px-4 py-2 text-sm rounded-full bg-white border border-black/12 hover:bg-black/5 font-semibold flex items-center gap-1.5"
            data-testid="membership-manage-btn"
          >
            Manage plan <ArrowRight size={14}/>
          </button>
        ) : paused ? (
          <div className="text-right">
            <button
              disabled
              className="px-4 py-2 text-sm rounded-full bg-black/[0.06] text-[#2D2926]/45 font-semibold cursor-not-allowed flex items-center gap-1.5"
              data-testid="membership-coming-soon-btn"
            >
              <Clock size={14}/> Pro Plus — Coming Soon
            </button>
            <button
              onClick={() => navigate("/upgrade")}
              className="block ml-auto mt-2 text-xs text-[#245544] font-semibold hover:underline"
              data-testid="membership-explore-btn"
            >
              See what's included →
            </button>
          </div>
        ) : (
          <button
            onClick={() => navigate("/upgrade")}
            className="px-4 py-2 text-sm rounded-full bg-white border border-[#2E6B57]/40 text-[#245544] hover:bg-[#E9F0EC] font-semibold flex items-center gap-1.5"
            data-testid="membership-explore-btn"
          >
            <Sparkles size={14}/> Explore Pro Plus
          </button>
        )}
      </div>
    </section>
  );
}
